import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSpinnerOverlayComponent } from 'src/app/components/mat-spinner-overlay/mat-spinner-overlay.component';
import * as XLSX from 'xlsx';
import { SnackBarService } from '../../services/snack-bar-service.service';
import { CriarNovoUsuarioComponent } from '../criar-novo-usuario/criar-novo-usuario.component';
import { EditarUsuarioMasterComponent } from '../editar-usuario-master/editar-usuario-master.component';
import { AdmRedePosto, PerfilMaster, UsuarioMaster } from '../models';
import { RemoverUsuarioMasterComponent } from '../remover-usuario-master/remover-usuario-master.component';
import { AdmRedePostosService } from '../services/adm-rede-postos.service';
import { StringNormalizer } from '../utils';

/**
 * Component for displaying and managing users table
 */
@Component({
  selector: 'app-tabela-usuarios',
  templateUrl: './tabela-usuarios.component.html',
  styleUrls: ['./tabela-usuarios.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatExpansionModule,
    MatSpinnerOverlayComponent,
  ],
})
export class TabelaUsuariosComponent implements OnInit, AfterViewInit {
  // ==================== Dependencies ====================
  private readonly dialog = inject(MatDialog);
  private readonly service = inject(AdmRedePostosService);
  private readonly snack = inject(SnackBarService);

  // ==================== Inputs/Outputs ====================
  @Input() admRedeselecionado: AdmRedePosto | undefined;
  @Output() novoUsuarioCriado = new EventEmitter<void>();
  @Output() removerUsuario = new EventEmitter<void>();
  @Output() totalUsuariosChange = new EventEmitter<number>();

  // ==================== ViewChild ====================
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // ==================== State ====================
  public loading: boolean = false;
  private _usuarios: UsuarioMaster[] = [];
  private _perfis: PerfilMaster[] = [];

  // ==================== Getters ====================
  get usuarios(): UsuarioMaster[] {
    return this._usuarios;
  }

  get perfis(): PerfilMaster[] {
    return this._perfis;
  }

  get totalUsuarios(): number {
    return this._usuarios.length;
  }

  // ==================== Data Source ====================
  readonly dataSource = new MatTableDataSource<UsuarioMaster>([]);

  // ==================== Forms ====================
  readonly filterForm = new FormGroup({
    dscNome: new FormControl(''),
    dscLogin: new FormControl(''),
    dscEmail: new FormControl(''),
    numDddFone: new FormControl(''),
    numFone: new FormControl(''),
    perfil: new FormControl(''),
  });

  // ==================== Lifecycle ====================
  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.setupDataSource();
  }

  // ==================== Setup Methods ====================
  private setupDataSource(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.configureSorting();
    this.configureFiltering();
  }

  private configureSorting(): void {
    this.dataSource.sortingDataAccessor = (
      data: UsuarioMaster,
      sortHeaderId: string
    ) => {
      switch (sortHeaderId) {
        case 'dscNome':
          return StringNormalizer.normalizeForSorting(data.dscNome);
        case 'dscLogin':
          return StringNormalizer.normalizeForSorting(data.dscLogin);
        case 'dscEmail':
          return StringNormalizer.normalizeForSorting(data.dscEmail);
        case 'numFone':
          return StringNormalizer.normalizeForSorting(data.telefoneCompleto);
        case 'perfil':
          return StringNormalizer.normalizeForSorting(data.descPerfil || '');
        default:
          return (data as any)[sortHeaderId] || '';
      }
    };
  }

  private configureFiltering(): void {
    this.dataSource.filterPredicate = (
      data: UsuarioMaster,
      filter: string
    ): boolean => {
      const filters = JSON.parse(filter);
      const normalize = StringNormalizer.normalizeForSearch;

      return (
        (!filters.dscNome ||
          normalize(data.dscNome).includes(normalize(filters.dscNome))) &&
        (!filters.dscLogin ||
          normalize(data.dscLogin).includes(normalize(filters.dscLogin))) &&
        (!filters.email ||
          normalize(data.dscEmail).includes(normalize(filters.email))) &&
        (!filters.numFone ||
          (data.numFone
            ? normalize(data.numFone).includes(normalize(filters.numFone))
            : false)) &&
        (!filters.perfil ||
          (data.codPerfil
            ? data.codPerfil.toString().includes(filters.perfil)
            : false))
      );
    };
  }

  // ==================== Data Loading ====================
  private async loadData(): Promise<void> {
    if (!this.admRedeselecionado) return;

    await this.loadPerfis();
    await this.loadUsuarios();
  }

  private async loadPerfis(): Promise<void> {
    if (!this.admRedeselecionado) return;

    this.loading = true;
    this.service
      .buscarListaPerfis(this.admRedeselecionado.codEntidade)
      .subscribe({
        next: (perfis) => {
          this._perfis = perfis;
        },
        error: () => {
          this.snack.flashMessage(
            'Erro ao carregar os perfis',
            ['snackbar-error'],
            'Fechar',
            3000
          );
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  private async loadUsuarios(): Promise<void> {
    if (!this.admRedeselecionado) return;

    this.loading = true;
    const payload = {
      codEntidade: `${this.admRedeselecionado.codEntidade}`,
      nome: this.admRedeselecionado.dscLoginEntidade,
    };

    this.service.buscarListaUsuario(payload).subscribe({
      next: (usuarios) => {
        this._usuarios = usuarios;
        this.dataSource.data = usuarios;
        this.reconfigureSort();
        this.emitTotalUsuarios();
      },
      error: () => {
        this.snack.flashMessage(
          'Erro ao carregar os usuários da rede',
          ['snackbar-error'],
          'Fechar',
          3000
        );
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  private reconfigureSort(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  private emitTotalUsuarios(): void {
    this.totalUsuariosChange.emit(this.totalUsuarios);
  }

  // ==================== Filter Methods ====================
  applyFilter(): void {
    const filterValues = this.filterForm.value;
    this.dataSource.filter = JSON.stringify(filterValues);
    this.dataSource.paginator?.firstPage();
  }

  clearFilter(): void {
    this.filterForm.reset();
    this.applyFilter();
  }

  // ==================== Dialog Methods ====================
  criarNovoUsuario(): void {
    const dialogRef = this.dialog.open(CriarNovoUsuarioComponent, {
      width: '40%',
      data: {
        admRedePosto: this.admRedeselecionado,
        perfis: this._perfis,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadUsuarios();
        this.novoUsuarioCriado.emit();
      }
    });
  }

  editarUsuario(usuario: UsuarioMaster): void {
    const dialogRef = this.dialog.open(EditarUsuarioMasterComponent, {
      width: '40%',
      data: {
        admRedePosto: this.admRedeselecionado,
        usuario: usuario,
        perfis: this._perfis,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.loadUsuarios();
    });
  }

  excluirUsuario(usuario: UsuarioMaster): void {
    const usuarioCompleto = {
      ...usuario.toJson(),
      dscLoginEntidade: this.admRedeselecionado?.dscLoginEntidade,
      codEntidade: this.admRedeselecionado?.codEntidade,
    };

    const dialogRef = this.dialog.open(RemoverUsuarioMasterComponent, {
      width: '50%',
      data: {
        usuario: usuarioCompleto,
        admRedePosto: this.admRedeselecionado,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const usuariosAtualizados = this._usuarios.filter(
          (u) => u.codLogin !== usuario.codLogin
        );
        this._usuarios = usuariosAtualizados;
        this.dataSource.data = usuariosAtualizados;

        this.snack.flashMessage(
          `Login: ${usuario.dscLogin} removido com sucesso.`,
          ['snackbar-success'],
          'Fechar',
          3000
        );
        this.emitTotalUsuarios();
      }
    });
  }

  // ==================== Export Methods ====================
  exportToExcel(): void {
    const data = this.prepareExcelData();
    const workSheet = XLSX.utils.json_to_sheet(data);
    const workBook = XLSX.utils.book_new();
    const fileName = this.service.gerarNomeArquivo('Usuarios-da-rede_');
    XLSX.utils.book_append_sheet(workBook, workSheet, fileName);
    XLSX.writeFile(workBook, `${fileName}.xlsx`);
  }

  private prepareExcelData(): Array<{
    Nome: string;
    Login: string;
    Email: string;
    Telefone: string;
    Perfil: string | undefined;
  }> {
    return this.dataSource.filteredData.map((item) => ({
      Nome: item.dscNome,
      Login: item.dscLogin,
      Email: item.dscEmail,
      Telefone: item.telefoneCompleto,
      Perfil: item.descPerfil,
    }));
  }
}
